import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { loginAdmin, verifyAdmin } from '../../api/adminAuth';
import { getActivityDetails } from '../../api/activities';
import { BeatLoader } from 'react-spinners';
import Cookies from 'js-cookie';

const AdminLogin = () => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [checkingAuth, setCheckingAuth] = useState(true);
  const [isVisible, setIsVisible] = useState(false);
  const [activityData, setActivityData] = useState(null);
  const navigate = useNavigate();
  const { activityId } = useParams();

  // Theme configuration based on activity type
  const isEventType = !activityData?.type || activityData?.type === 'event';

  useEffect(() => {
    const checkAuth = async () => {
      const token = Cookies.get('adminToken');
      if (token) {
        try {
          const response = await verifyAdmin(token);
          if (response.success) {
            navigate(`/admin/${activityId}`);
          } else {
            setError('Invalid credentials');
          }
        } catch (err) {
          // Not authenticated, show login form
        }
      }
      
      // Fetch activity details to determine theme
      try {
        const activityResponse = await getActivityDetails(activityId);
        if (activityResponse?.success && activityResponse?.data) {
          setActivityData(activityResponse.data);
        }
      } catch (err) {
        console.error('Failed to fetch activity details:', err);
      }
      
      setCheckingAuth(false);
      // Show entrance animation
      setTimeout(() => setIsVisible(true), 100);
    };
    checkAuth();
  }, [activityId, navigate]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      const response = await loginAdmin(username, password, activityId);
      if (response.data.success) {
        navigate(`/admin/${activityId}`);
      } else {
        setError('Invalid credentials');
      }
    } catch (error) {
      setError('Invalid credentials');
    } finally {
      setIsLoading(false);
    }
  };

  if (checkingAuth) {
    return (
      <div className={`min-h-screen flex items-center justify-center ${
        isEventType
          ? 'bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50'
          : 'bg-gradient-to-br from-slate-900 via-indigo-900 to-purple-900'
      }`}>
        <div className="text-center">
          <div className="mb-6">
            <div className={`w-16 h-16 mx-auto rounded-full flex items-center justify-center ${
              isEventType ? 'bg-indigo-100' : 'bg-indigo-800'
            }`}>
              <svg className="w-8 h-8 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
              </svg>
            </div>
          </div>
          <BeatLoader
            color={isEventType ? "#4f46e5" : "#8b5cf6"}
            loading={checkingAuth}
            size={12}
            aria-label="Loading Spinner"
          />
          <p className={`mt-4 font-medium text-sm ${
            isEventType ? 'text-slate-600' : 'text-slate-300'
          }`}>
            Authenticating...
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className={`min-h-screen relative ${
      isEventType
        ? 'bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50'
        : 'bg-gradient-to-br from-slate-900 via-indigo-900 to-purple-900'
    }`}>
      {/* Subtle geometric background elements */}
      <div className="absolute inset-0 pointer-events-none">
        <div className={`absolute top-20 left-10 w-32 h-32 rounded-full opacity-10 ${
          isEventType ? 'bg-indigo-200' : 'bg-indigo-400'
        }`}></div>
        <div className={`absolute bottom-40 right-20 w-24 h-24 rounded-full opacity-10 ${
          isEventType ? 'bg-blue-200' : 'bg-purple-400'
        }`}></div>
        <div className={`absolute top-1/2 left-1/4 w-16 h-16 rounded-full opacity-10 ${
          isEventType ? 'bg-purple-200' : 'bg-blue-400'
        }`}></div>
      </div>

      <div className={`relative z-10 min-h-screen flex items-center justify-center p-4 transition-all duration-500 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
        <div className="w-full max-w-md">
          <div className={`backdrop-blur-sm rounded-xl shadow-xl p-8 border transition-all duration-300 hover:shadow-2xl ${
            isEventType 
              ? 'bg-white/95 border-slate-200'
              : 'bg-slate-800/95 border-slate-700'
          }`}>
            <div className="text-center mb-8">
              <div className={`w-16 h-16 mx-auto mb-4 rounded-full flex items-center justify-center ${
                isEventType ? 'bg-indigo-100' : 'bg-indigo-800'
              }`}>
                <svg className="w-8 h-8 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                </svg>
              </div>
              <h2 className={`text-2xl font-bold mb-2 ${
                isEventType ? 'text-slate-900' : 'text-white'
              }`}>
                Admin Login
              </h2>
              <p className={`text-sm ${
                isEventType ? 'text-slate-600' : 'text-slate-300'
              }`}>
                {isEventType 
                  ? 'Welcome back. Please sign in to your admin account.'
                  : 'Welcome back. Please sign in to your admin account.'
                }
              </p>
            </div>

            {error && (
              <div className={`mb-6 p-4 rounded-lg flex items-center border ${
                isEventType
                  ? 'bg-red-50 text-red-700 border-red-200'
                  : 'bg-red-900/20 text-red-400 border-red-800'
              }`}>
                <svg className="w-5 h-5 mr-3 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                </svg>
                <div>
                  <p className="font-medium text-sm">Authentication Failed</p>
                  <p className="text-xs mt-1">{error}</p>
                </div>
              </div>
            )}

            <form onSubmit={handleLogin} className="space-y-6">
              <div>
                <label className={`block text-sm font-medium mb-2 ${
                  isEventType ? 'text-slate-700' : 'text-slate-300'
                }`} htmlFor="username">
                  Username
                </label>
                <input
                  type="text"
                  id="username"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  className={`w-full px-4 py-3 rounded-lg border focus:outline-none focus:ring-2 transition-all duration-200 ${
                    isEventType
                      ? 'border-slate-300 bg-white text-slate-900 focus:ring-indigo-500 focus:border-indigo-500'
                      : 'border-slate-600 bg-slate-700 text-white focus:ring-indigo-400 focus:border-indigo-400'
                  }`}
                  placeholder="Enter your username"
                  required
                />
              </div>
              <div>
                <label className={`block text-sm font-medium mb-2 ${
                  isEventType ? 'text-slate-700' : 'text-slate-300'
                }`} htmlFor="password">
                  Password
                </label>
                <input
                  type="password"
                  id="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className={`w-full px-4 py-3 rounded-lg border focus:outline-none focus:ring-2 transition-all duration-200 ${
                    isEventType
                      ? 'border-slate-300 bg-white text-slate-900 focus:ring-indigo-500 focus:border-indigo-500'
                      : 'border-slate-600 bg-slate-700 text-white focus:ring-indigo-400 focus:border-indigo-400'
                  }`}
                  placeholder="Enter your password"
                  required
                />
              </div>
              <button
                type="submit"
                disabled={isLoading}
                className={`w-full py-3 rounded-lg font-medium text-white shadow-lg hover:shadow-xl transform transition-all duration-200 hover:scale-[1.02] focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none ${
                  isEventType
                    ? 'bg-indigo-600 hover:bg-indigo-700 focus:ring-indigo-500'
                    : 'bg-indigo-600 hover:bg-indigo-700 focus:ring-indigo-400'
                }`}
              >
                {isLoading ? (
                  <div className="flex items-center justify-center gap-3">
                    <BeatLoader color="#ffffff" size={8} />
                    <span>Signing in...</span>
                  </div>
                ) : (
                  <span>Sign In</span>
                )}
              </button>
            </form>

            {/* Footer */}
            <div className="mt-8 text-center">
              <div className={`rounded-lg p-4 border ${
                isEventType
                  ? 'bg-slate-50 border-slate-200'
                  : 'bg-slate-800/50 border-slate-700'
              }`}>
                <svg className="w-6 h-6 mx-auto mb-2 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                </svg>
                <p className={`font-medium text-sm ${
                  isEventType ? 'text-slate-600' : 'text-slate-300'
                }`}>
                  Secure admin access
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminLogin;